import React, { ReactNode } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { Role } from '../types/permissions';

interface PermissionGateProps {
  children: ReactNode;
  permission?: string;
  requiredRole?: Role;
  resourceType?: 'partner' | 'tenant';
  resourceId?: string;
  fallback?: ReactNode;
  showForAdmin?: boolean;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  permission,
  requiredRole,
  resourceType,
  resourceId,
  fallback = null,
  showForAdmin = true,
}) => {
  const { hasPermission, hasRoleAccess, isAdmin, loading } = usePermissions();

  // Show loading state
  if (loading) {
    return <div>Loading...</div>;
  }

  // Global admin bypass (unless explicitly disabled)
  if (isAdmin && showForAdmin) {
    return <>{children}</>;
  }

  // Check permission-based access
  if (permission) {
    if (hasPermission(permission, resourceType, resourceId)) {
      return <>{children}</>;
    }
    return <>{fallback}</>;
  }

  // Check role-based access
  if (requiredRole) {
    if (hasRoleAccess(requiredRole, resourceType, resourceId)) {
      return <>{children}</>;
    }
    return <>{fallback}</>;
  }

  // If no specific checks, show content
  return <>{children}</>;
};

// Convenience components for common permission checks
export const AdminOnly: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ 
  children, 
  fallback 
}) => (
  <PermissionGate requiredRole="admin" fallback={fallback}>
    {children}
  </PermissionGate>
);

export const OperatorOrHigher: React.FC<{ 
  children: ReactNode; 
  fallback?: ReactNode;
  resourceType?: 'partner' | 'tenant';
  resourceId?: string;
}> = ({ children, fallback, resourceType, resourceId }) => (
  <PermissionGate 
    requiredRole="operator" 
    fallback={fallback}
    resourceType={resourceType}
    resourceId={resourceId}
  >
    {children}
  </PermissionGate>
);

export const ViewerOrHigher: React.FC<{ 
  children: ReactNode; 
  fallback?: ReactNode;
  resourceType?: 'partner' | 'tenant';
  resourceId?: string;
}> = ({ children, fallback, resourceType, resourceId }) => (
  <PermissionGate 
    requiredRole="viewer" 
    fallback={fallback}
    resourceType={resourceType}
    resourceId={resourceId}
  >
    {children}
  </PermissionGate>
);

export const GlobalAdminOnly: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({
  children,
  fallback = null
}) => {
  const { isAdmin, loading } = usePermissions();
  if (loading) return <div>Loading...</div>;
  if (!isAdmin) return <>{fallback}</>;
  return <>{children}</>;
}; 